import { Routes, Route, NavLink } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { FeedView } from './views/FeedView';
import { InsightView } from './views/InsightView';
import { SearchView } from './views/SearchView';
import { SourcesView } from './views/SourcesView';
import { ReviewView } from './views/ReviewView';
import { TelegramView } from './views/TelegramView';
import { CommandPalette } from './components/CommandPalette';
import { useAuth } from './contexts/AuthContext';
import { LoginView } from './views/LoginView';

const NAV_ITEMS = [
  { to: '/', label: 'Feed', icon: '◉' },
  { to: '/search', label: 'Search', icon: '⌕' },
  { to: '/telegram', label: 'Telegram', icon: '✈' },
  { to: '/sources', label: 'Sources', icon: '⟳' },
  { to: '/review', label: 'Review', icon: '⋯' },
];

export function App() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setCommandPaletteOpen((prev) => !prev);
    }
    if (e.key === 'Escape') {
      setCommandPaletteOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-obsidian-bg">
        <p className="text-sm text-obsidian-text-muted">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView />;
  }

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <nav className="w-56 flex-shrink-0 bg-obsidian-bg-secondary border-r border-obsidian-border flex flex-col">
        <div className="p-4 border-b border-obsidian-border">
          <h1 className="text-sm font-semibold text-obsidian-text tracking-wide">
            Claude Code Insights
          </h1>
        </div>

        {/* Search trigger */}
        <button
          onClick={() => setCommandPaletteOpen(true)}
          className="mx-3 mt-3 flex items-center gap-2 px-3 py-1.5 text-sm text-obsidian-text-muted bg-obsidian-bg-tertiary border border-obsidian-border rounded-lg hover:border-obsidian-border-light transition-colors"
        >
          <span>⌕</span>
          <span className="flex-1 text-left">Search...</span>
          <kbd className="text-xs text-obsidian-text-faint">⌘K</kbd>
        </button>

        {/* Navigation */}
        <div className="flex-1 mt-4 space-y-0.5 px-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-obsidian-surface text-obsidian-text'
                    : 'text-obsidian-text-muted hover:bg-obsidian-bg-tertiary hover:text-obsidian-text'
                }`
              }
            >
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-obsidian-border">
          <button
            onClick={logout}
            className="text-xs text-obsidian-text-faint hover:text-obsidian-text transition-colors"
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<FeedView />} />
          <Route path="/insight/:id" element={<InsightView />} />
          <Route path="/search" element={<SearchView />} />
          <Route path="/telegram" element={<TelegramView />} />
          <Route path="/sources" element={<SourcesView />} />
          <Route path="/review" element={<ReviewView />} />
        </Routes>
      </main>

      {/* Command palette */}
      {commandPaletteOpen && (
        <CommandPalette onClose={() => setCommandPaletteOpen(false)} />
      )}
    </div>
  );
}
